import moment from 'moment';

import { AbsenceAttachment, LongAbsence, ShortAbsence } from '~/data/absences';
import {
  AbsenceEntry,
  AbsenceType,
  AttachmentInfo,
  LongAbsenceEntry,
  ShortAbsenceEntry,
} from '~/data/absences/types';
import { Enum } from '~/data/enums';
import { getFormattedDate } from '~/scenes/MyWorkspace/components/Widgets/components/ApprovalWidget/data';

const getAbsenceType = (
  absence?: LongAbsence | ShortAbsence,
): AbsenceType | undefined => {
  if (!absence) {
    return undefined;
  }

  const propKeys = Object.keys(absence);
  if (propKeys.find((k) => k.startsWith('fAbsenceShort'))) {
    return AbsenceType.SHORT;
  }
  if (propKeys.find((k) => k.startsWith('fAbsenceLong'))) {
    return AbsenceType.LONG;
  }

  return undefined;
};

export const filterAbsenceEntriesByType = (
  entries: AbsenceEntry[],
  type: AbsenceType,
) => entries.filter((e) => e.type === type);

const convertAbsenceAttachmentToAbsenceEntryAttachment = (
  attachment: AbsenceAttachment,
): AttachmentInfo => {
  const {
    fAttachmentId,
    fAttachmentName,
    fAttachmentType,
    fAttachmentIsArchived,
  } = attachment;

  return {
    id: fAttachmentId,
    name: fAttachmentName,
    type: fAttachmentType,
    isArchived: fAttachmentIsArchived,
  };
};

const convertLongAbsenceToLongAbsenceEntry = (
  longAbsence: LongAbsence,
  longAbsenceReasons: Enum[],
): LongAbsenceEntry => {
  const {
    fAbsenceLongUId,
    fAbsenceLongAttachment,
    fAbsenceLongEndDate,
    fAbsenceLongInfo,
    fAbsenceLongReason,
    fAbsenceLongStartDate,
    fAbsenceLongStatus,
    fAbsenceLongDeciderEmpId,
    fAbsenceLongDeciderName,
    fAbsenceLongDecidedDate,
    fAbsenceLongEmpId,
    fAbsenceLongId,
    fAbsenceLongEnteredUserName,
    fAbsenceLongEnteredTime,
    fAbsenceLongSupersederName,
    fAbsenceLongSupersededReason,
    fAbsenceLongSupersededTime,
  } = longAbsence;

  return {
    type: AbsenceType.LONG,
    id: fAbsenceLongId ? parseInt(fAbsenceLongId) : undefined,
    uid: fAbsenceLongUId,
    endDate: fAbsenceLongEndDate
      ? moment(fAbsenceLongEndDate).toDate()
      : undefined,
    info: fAbsenceLongInfo,
    reason: longAbsenceReasons.find(
      (e) => e.code === fAbsenceLongReason,
    ) as Enum, // TODO
    startDate: moment(fAbsenceLongStartDate).toDate(),
    status: fAbsenceLongStatus,
    attachment: convertAbsenceAttachmentToAbsenceEntryAttachment(
      fAbsenceLongAttachment,
    ),
    deciderEmpId: fAbsenceLongDeciderEmpId,
    deciderName: fAbsenceLongDeciderName,
    decidedDate: fAbsenceLongDecidedDate
      ? moment(fAbsenceLongDecidedDate).toDate()
      : undefined,
    empId: fAbsenceLongEmpId,
    enteredUserName: fAbsenceLongEnteredUserName,
    enteredTime: fAbsenceLongEnteredTime
      ? moment(fAbsenceLongEnteredTime).toDate()
      : undefined,
    supersededName: fAbsenceLongSupersederName,
    supersededReason: fAbsenceLongSupersededReason,
    supersededDate: fAbsenceLongSupersededTime
      ? moment(fAbsenceLongSupersededTime).toDate()
      : undefined,
  };
};

const convertShortAbsenceToShortAbsenceEntry = (
  shortAbsence: ShortAbsence,
  shortAbsenceReasons: Enum[],
): ShortAbsenceEntry => {
  const {
    fAbsenceShortUId,
    fAbsenceShortAttachment,
    fAbsenceShortDays,
    fAbsenceShortBalanceYear,
    fAbsenceShortAbsenceHours,
    fAbsenceShortPlannedWorkingHours,
    fAbsenceShortEndDate,
    fAbsenceShortInfo,
    fAbsenceShortReason,
    fAbsenceShortStartDate,
    fAbsenceShortStatus,
    fAbsenceShortDeciderEmpId,
    fAbsenceShortDeciderName,
    fAbsenceShortDecidedDate,
    fAbsenceShortEmpId,
    fAbsenceShortId,
    fAbsenceShortEnteredUserName,
    fAbsenceShortEnteredTime,
    fAbsenceShortSupersederName,
    fAbsenceShortSupersededReason,
    fAbsenceShortSupersededTime,
  } = shortAbsence;

  return {
    type: AbsenceType.SHORT,
    id: fAbsenceShortId ? parseInt(fAbsenceShortId) : undefined,
    uid: fAbsenceShortUId,
    days: fAbsenceShortDays,
    year: fAbsenceShortBalanceYear,
    hours: fAbsenceShortAbsenceHours,
    plannedWorkingHours: fAbsenceShortPlannedWorkingHours,
    endDate: moment(fAbsenceShortEndDate).toDate(),
    info: fAbsenceShortInfo,
    reason: shortAbsenceReasons.find(
      (e) => e.code === fAbsenceShortReason,
    ) as Enum, // TODO
    startDate: moment(fAbsenceShortStartDate).toDate(),
    status: fAbsenceShortStatus,
    attachment: convertAbsenceAttachmentToAbsenceEntryAttachment(
      fAbsenceShortAttachment,
    ),
    deciderEmpId: fAbsenceShortDeciderEmpId,
    deciderName: fAbsenceShortDeciderName,
    decidedDate: fAbsenceShortDecidedDate
      ? moment(fAbsenceShortDecidedDate).toDate()
      : undefined,
    empId: fAbsenceShortEmpId,
    enteredUserName: fAbsenceShortEnteredUserName,
    enteredTime: fAbsenceShortEnteredTime
      ? moment(fAbsenceShortEnteredTime).toDate()
      : undefined,
    supersededName: fAbsenceShortSupersederName,
    supersededReason: fAbsenceShortSupersededReason,
    supersededDate: fAbsenceShortSupersededTime
      ? moment(fAbsenceShortSupersededTime).toDate()
      : undefined,
  };
};

export const mapAPIAbsencesToAbsenceEntries = (
  absences: (LongAbsence | ShortAbsence)[],
  longAbsenceReasons: Enum[],
  shortAbsenceReasons: Enum[],
): AbsenceEntry[] => {
  return absences
    .reduce((converted, absence) => {
      const type = getAbsenceType(absence);

      switch (type) {
        case AbsenceType.LONG:
          return [
            ...converted,
            convertLongAbsenceToLongAbsenceEntry(
              absence as LongAbsence,
              longAbsenceReasons,
            ),
          ];
        case AbsenceType.SHORT:
          return [
            ...converted,
            convertShortAbsenceToShortAbsenceEntry(
              absence as ShortAbsence,
              shortAbsenceReasons,
            ),
          ];
        default:
          return converted;
      }
    }, [] as (LongAbsenceEntry | ShortAbsenceEntry)[])
    .sort((a, b) => moment(b.startDate).diff(moment(a.startDate)));
};

const convertAbsenceEntryAttachmentToAbsenceAttachment = (
  attachment: AttachmentInfo,
): AbsenceAttachment => {
  const { id, name, type, isArchived } = attachment;

  return {
    fAttachmentId: id,
    fAttachmentName: name,
    fAttachmentType: type,
    fAttachmentIsArchived: isArchived,
  };
};

const convertLongAbsenceEntryToLongAbsence = (
  longAbsenceEntry: LongAbsenceEntry,
): LongAbsence => {
  const {
    uid,
    attachment,
    endDate,
    info,
    reason,
    startDate,
    status,
    deciderEmpId,
    deciderName,
    decidedDate,
    empId,
    id,
    enteredUserName,
    enteredTime,
  } = longAbsenceEntry;

  const formatAPIDate = (date: Date | undefined) =>
    !!date ? getFormattedDate(date, 'YYYY-MM-DD') : '';

  return {
    fAbsenceLongUId: uid,
    fAbsenceLongAttachment:
      convertAbsenceEntryAttachmentToAbsenceAttachment(attachment),
    fAbsenceLongEndDate: formatAPIDate(endDate),
    fAbsenceLongInfo: info,
    fAbsenceLongReason: reason?.code,
    fAbsenceLongStartDate: formatAPIDate(startDate),
    fAbsenceLongStatus: status,
    fAbsenceLongDeciderEmpId: deciderEmpId,
    fAbsenceLongDeciderName: deciderName,
    fAbsenceLongDecidedDate: formatAPIDate(decidedDate),
    fAbsenceLongEmpId: empId,
    fAbsenceLongId: id ? id.toString() : undefined,
    fAbsenceLongEnteredUserName: enteredUserName,
    fAbsenceLongEnteredTime: enteredTime?.toISOString(),
  };
};

const convertShortAbsenceEntryToShortAbsence = (
  shortAbsenceEntry: ShortAbsenceEntry,
): ShortAbsence => {
  const {
    uid,
    attachment,
    days,
    year,
    hours,
    plannedWorkingHours,
    endDate,
    info,
    reason,
    startDate,
    status,
    deciderEmpId,
    deciderName,
    decidedDate,
    empId,
    id,
    enteredUserName,
    enteredTime,
  } = shortAbsenceEntry;

  const formatAPIDate = (date: Date | undefined) =>
    !!date ? getFormattedDate(date, 'YYYY-MM-DD') : '';

  return {
    fAbsenceShortUId: uid,
    fAbsenceShortAttachment:
      convertAbsenceEntryAttachmentToAbsenceAttachment(attachment),
    fAbsenceShortDays: days,
    fAbsenceShortBalanceYear: year,
    fAbsenceShortAbsenceHours: hours,
    fAbsenceShortPlannedWorkingHours: plannedWorkingHours,
    fAbsenceShortEndDate: formatAPIDate(endDate),
    fAbsenceShortInfo: info,
    fAbsenceShortReason: reason?.code,
    fAbsenceShortStartDate: formatAPIDate(startDate),
    fAbsenceShortStatus: status,
    fAbsenceShortDeciderEmpId: deciderEmpId,
    fAbsenceShortDeciderName: deciderName,
    fAbsenceShortDecidedDate: formatAPIDate(decidedDate),
    fAbsenceShortEmpId: empId,
    fAbsenceShortId: id ? id.toString() : undefined,
    fAbsenceShortEnteredUserName: enteredUserName,
    fAbsenceShortEnteredTime: enteredTime?.toString(),
  };
};

export const convertAbsenceEntryToAbsence = (
  entry: AbsenceEntry,
): ShortAbsence | LongAbsence => {
  return entry.type === AbsenceType.SHORT
    ? convertShortAbsenceEntryToShortAbsence(entry as ShortAbsenceEntry)
    : convertLongAbsenceEntryToLongAbsence(entry as LongAbsenceEntry);
};
